import cn from 'classnames';
import * as React from 'react';
import { mergeClasses } from '@tcl-argon-utils/utils';
import Loader from '@tcl-boron-prefabs/loader';
import { useState } from 'react';
import { uniqueId } from 'lodash-es';
import styles from './baseButton.module.css';
export interface ButtonColors {
  color: string;
  backgroundColor: string;
  borderColor: string;
  borderStyle: string;
}

export interface BaseButtonProps
  extends React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> {
  /** Map of rule names and generated class names */
  classes?: Record<string, string>;
  /** Provide a customized loader element if necessary */
  Loader?: React.ReactNode;
  /** If `true`, the button is disabled and displays a spinning loader in place of its normal content. */
  loading?: boolean;
  /** A set of colors to be applied when the button is active/pressed */
  pressedColors: ButtonColors;
  /** A set of colors to be applied when the button is in focus */
  focusColors: ButtonColors;
  /** A set of colors to be applied to a button in its default state*/
  defaultColors: ButtonColors;
  /** A set of colors to be applied when the button is hovered */
  hoverColors: ButtonColors;
  /** A set of colors to be applied when the button is disabled */
  disabledColors: ButtonColors;
  /** If `true`, style button to be small. If `false`, style button to be default size. */
  small?: boolean;
  /** minimum width in px */
  minWidth?: string;
  /** If `true`, change text in button to be all caps and have larger line spacing. If `false`, button text will be however it is entered */
  allCaps?: boolean;
  /** label passed to button for screen reader use */
  ariaLabel: string;
}

const defaultLoader = <Loader size="large" />;

export const BaseButton: React.FC<BaseButtonProps> = ({
  Loader = defaultLoader,
  pressedColors,
  focusColors,
  disabledColors,
  children,
  className = '',
  classes: classOverrides = {},
  defaultColors,
  disabled,
  hoverColors,
  loading = false,
  onClick = () => {},
  small = false,
  type = 'button',
  minWidth,
  allCaps = false,
  ariaLabel,
  id,
  ...props
}) => {
  [id] = useState(id || uniqueId('base_button_'));

  const classes = mergeClasses(styles, classOverrides);
  const isDisabled = disabled || loading;

  return (
    <button
      type={type}
      className={cn(classes.root, className, { [classes.disabled]: isDisabled })}
      onClick={onClick}
      disabled={isDisabled}
      aria-label={ariaLabel}
      key={id}
      id={id}
      {...props}
      style={
        {
          '--tcl-min-width': minWidth === undefined ? '' : minWidth,
          '--tcl-text-transform': allCaps ? 'uppercase' : 'none',
          '--tcl-letter-spacing': allCaps ? '2px' : 'none',
          '--tcl-button-height': small ? '32px' : '48px',
          '--tcl-default-color': defaultColors.color,
          '--tcl-default-background-color': defaultColors.backgroundColor,
          '--tcl-default-border-color': defaultColors.borderColor,
          '--tcl-hover-color': hoverColors.color,
          '--tcl-hover-background-color': hoverColors.backgroundColor,
          '--tcl-hover-border-color': hoverColors.borderColor,
          '--tcl-focus-color': focusColors.color,
          '--tcl-focus-background-color': focusColors.backgroundColor,
          '--tcl-focus-border-color': focusColors.borderColor,
          '--tcl-pressed-color': pressedColors.color,
          '--tcl-pressed-background-color': pressedColors.backgroundColor,
          '--tcl-pressed-border-color': pressedColors.borderColor,
          '--tcl-disabled-color': disabledColors.color,
          '--tcl-disabled-background-color': disabledColors.backgroundColor,
          '--tcl-disabled-border-color': disabledColors.borderColor,
        } as React.CSSProperties
      }
    >
      {loading && <div className={classes.loader}>{Loader}</div>}
      <div className={cn(classes.children, { [classes.loading]: loading })}>{children}</div>
    </button>
  );
};
